import axios, { type InternalAxiosRequestConfig } from "axios";
import { env } from "./env";
import { currentPath, rememberReturnTo } from "./sessionRedirect";

const ACCESS_TOKEN_KEY = "provisioning-portal/access-token";

export const tokenStorage = {
  get: (): string | null => {
    if (typeof window === "undefined") {
      return null;
    }
    return window.localStorage.getItem(ACCESS_TOKEN_KEY);
  },
  set: (token: string) => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(ACCESS_TOKEN_KEY, token);
  },
  remove: () => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.removeItem(ACCESS_TOKEN_KEY);
  }
};

export const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
  headers: {
    "Content-Type": "application/json"
  }
});

function attachAuth(config: InternalAxiosRequestConfig) {
  const token = tokenStorage.get();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}

apiClient.interceptors.request.use(attachAuth);

const LOGIN_PATH = "/auth/login/access-token";

/**
 * An expired or revoked session answers 401. Drop the stored token, remember the page, and
 * tell AuthContext to sign out so the router shows the sign-in page instead of pages that
 * keep "Loading…". A failed sign-in attempt (wrong password) is left to the login form.
 */
export function handleUnauthorized(error: unknown): Promise<never> {
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    const url = error.config?.url ?? "";
    if (!url.includes(LOGIN_PATH) && tokenStorage.get()) {
      tokenStorage.remove();
      rememberReturnTo(currentPath());
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("auth:logout"));
      }
    }
  }
  return Promise.reject(error);
}

apiClient.interceptors.response.use(undefined, handleUnauthorized);
